<!-- PodcastControls.vue -->
<template>
    <div class="podcast-controls-container" :class="{ 'is-playing': isPlaying }">
      <!-- Modern Timeline with Speaker Preview -->
      <div class="timeline-section">
        <div class="segment-preview">
          <div class="current-segment-indicator">
            <span class="segment-count">{{ activeSegment + 1 }}/{{ totalSegments }}</span>
            <span class="current-speaker" v-if="currentSpeaker">
              {{ currentSpeaker }} <span class="speaker-role" v-if="currentRole">({{ currentRole }})</span>
            </span>
          </div>

          <!-- Current time / Total duration -->
          <div class="time-display">
            <span class="current-time">{{ formatTime(currentTime) }}</span>
            <span class="time-separator">/</span>
            <span class="total-time">{{ estimatedLength }}</span>
          </div>
        </div>

        <!-- Interactive Timeline -->
        <div class="progress-timeline" @click="seek">
          <div class="progress-segments-container">
            <!-- Render segment markers to visualize different speakers -->
            <div
              v-for="(segment, idx) in segments"
              :key="idx"
              class="segment-marker"
              :class="{
                'active': idx === activeSegment,
                'completed': idx < activeSegment
              }"
              :style="{
                width: getSegmentWidth(idx),
                backgroundColor: getSpeakerColor(segment.speaker, segment.role)
              }"
            ></div>
          </div>
          <div class="timeline-progress" :style="{ width: `${progressPercentage}%` }"></div>
          <div class="timeline-handle" :style="{ left: `${progressPercentage}%` }"></div>
        </div>
      </div>

      <!-- Modern Playback Controls -->
      <div class="control-deck">
        <!-- Secondary Controls with More Compact Rate Buttons -->
        <div class="secondary-controls">
          <div class="compact-speed-control">
            <div class="speed-buttons">
              <button
                v-for="speed in availableSpeeds"
                :key="speed"
                class="compact-speed-btn"
                :class="{ active: voiceSpeed === speed }"
                @click="changeSpeed(speed)"
              >{{ speed }}x</button>
            </div>
          </div>
        </div>

        <!-- Main Controls -->
        <div class="main-controls">
          <button class="control-btn rewind-btn" @click="skipBackward" aria-label="Previous segment">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="19 20 9 12 19 4 19 20"></polygon>
              <line x1="5" y1="19" x2="5" y2="5"></line>
            </svg>
          </button>

          <button class="play-btn" @click="togglePlay" aria-label="Play or pause">
            <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
          </button>

          <button class="control-btn forward-btn" @click="skipForward" aria-label="Next segment">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 4 15 12 5 20 5 4"></polygon>
              <line x1="19" y1="5" x2="19" y2="19"></line>
            </svg>
          </button>
        </div>

        <!-- Extra Controls -->
        <div class="extra-controls">
          <!-- Download button -->
          <button
            class="control-toggle-btn download-btn"
            :class="{
              'is-downloading': isDownloading,
              'is-ready': downloadReady
            }"
            @click="downloadReady ? triggerDownload() : handleDownload()"
            :disabled="isDownloading"
            aria-label="Download conversation"
          >
            <svg v-if="!isDownloading && !downloadReady" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <svg v-else-if="downloadReady" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <div v-else class="download-progress-ring">
              <svg width="18" height="18" viewBox="0 0 24 24">
                <circle
                  cx="12" cy="12" r="10"
                  fill="none" stroke="currentColor" opacity="0.3"
                  stroke-width="2"
                />
                <circle
                  cx="12" cy="12" r="10"
                  fill="none" stroke="currentColor"
                  stroke-width="2"
                  stroke-dasharray="62.83"
                  :stroke-dashoffset="dashOffset"
                  transform="rotate(-90 12 12)"
                />
              </svg>
              <span class="progress-text">{{ Math.round(downloadProgress) }}%</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </template>

  <script>
  import ProFeatureChip from './ProFeatureChip.vue';
  import { mapActions } from 'vuex';

  export default {
    name: 'PodcastControls',
    components: {
      ProFeatureChip
    },
    props: {
      isPro: {
        type: Boolean,
        default: false
      },
      isPlaying: {
        type: Boolean,
        default: false
      },
      isMobile: {
        type: Boolean,
        default: false
      },
      isExpandedView: {
        type: Boolean,
        default: true
      },
      activeSegment: {
        type: Number,
        default: 0
      },
      totalSegments: {
        type: Number,
        required: true
      },
      segments: {
        type: Array,
        default: () => []
      },
      currentTime: {
        type: Number,
        default: 0
      },
      estimatedLength: {
        type: String,
        default: '0:00'
      },
      voiceSpeed: {
        type: Number,
        default: 1.33
      },
      progressPercentage: {
        type: Number,
        default: 0
      },
      isDownloading: {
        type: Boolean,
        default: false
      },
      downloadProgress: {
        type: Number,
        default: 0
      },
      downloadReady: {
        type: Boolean,
        default: false
      },
      currentSpeaker: {
        type: String,
        default: ''
      },
      currentRole: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        showHighlight: false,
        availableSpeeds: [1.0, 1.2, 1.33, 1.5, 2.0],
        speakerColors: {
          host: '#4299e1',     // Blue
          expert: '#805ad5',   // Purple
          researcher: '#38a169', // Green
          analyst: '#ed8936',  // Orange
          journalist: '#e53e3e', // Red
          default: '#718096'   // Gray
        },
        // Cache for segment durations
        segmentDurations: null
      }
    },
    computed: {
      dashOffset() {
        // Calculate the stroke-dashoffset based on download progress
        const circumference = 62.83; // 2 * Math.PI * 10
        return circumference - (this.downloadProgress / 100) * circumference;
      },

      // Calculate segment durations and total duration
      cumulativeSegmentDurations() {
        if (!this.segments || this.segments.length === 0) {
          return [];
        }

        if (this.segmentDurations === null) {
          this.calculateSegmentDurations();
        }

        return this.segmentDurations;
      },

      // Total estimated duration in seconds
      totalDuration() {
        if (!this.segments || this.segments.length === 0) {
          return 0;
        }

        if (this.segmentDurations === null) {
          this.calculateSegmentDurations();
        }

        return this.segmentDurations[this.segmentDurations.length - 1].endTime;
      }
    },
    watch: {
      segments: {
        handler() {
          // Reset segment durations when segments change
          this.segmentDurations = null;
        },
        deep: true
      },
      voiceSpeed() {
        // Recalculate segment durations when speed changes
        this.segmentDurations = null;
      }
    },
    mounted() {
      // Initialize segment durations
      this.checkCustomizationNeeded();
    },
    methods: {
      ...mapActions('websocket', ['showTempMessageFn']),
      checkCustomizationNeeded() {
        // If user not pro, show highlight animation
        if (!this.isPro) {
          this.showHighlight = true;
          setTimeout(() => {
            this.showHighlight = false;
          }, 2000000);
        }
      },
      toggleExpandedView() {
        if (!this.isPro) {
          this.showTempMessageFn({
            message: 'Upgrade to Pro to view the full conversation history.',
            type: 'warning'
          });
          return;
        }
        this.$emit('toggle-expanded-view');
      },
      togglePlay() {
        this.$emit('toggle-play');
      },
      skipBackward() {
        this.$emit('skip-backward');
      },
      skipForward() {
        this.$emit('skip-forward');
      },
      seek(event) {
        this.$emit('seek', event);
      },
      changeSpeed(speed) {
        // only for pro users
        this.$emit('speed-change', speed);
      },
      handleDownload() {
        // only for pro users
        if (!this.isPro) {
          this.showTempMessageFn({
            message: 'Upgrade to Pro to download the conversation.',
            type: 'warning'
          });
          return;
        }
        this.$emit('download');
      },
      triggerDownload() {
        // only for pro users
        if (!this.isPro) {
          this.showTempMessageFn({
            message: 'Upgrade to Pro to download the conversation.',
            type: 'warning'
          });
          return;
        }
        this.$emit('trigger-download');
      },
      formatTime(seconds) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
      },
      getSpeakerColor(speaker, role) {
        if (!role) return this.speakerColors.default;

        // Check for role match
        const normalizedRole = role.toLowerCase();
        for (const [key, color] of Object.entries(this.speakerColors)) {
          if (normalizedRole.includes(key)) {
            return color;
          }
        }

        // Fallback to default color
        return this.speakerColors.default;
      },

      // Calculate durations for each segment based on word count
      calculateSegmentDurations() {
        // Initialize array to store segment info
        this.segmentDurations = [];

        let cumulativeTime = 0;

        // Calculate duration for each segment
        this.segments.forEach((segment, index) => {
          // Estimate duration based on words (approximation)
          let wordCount = 0;

          if (segment.content) {
            // Extract text from segment content, removing HTML tags
            const text = segment.content.replace(/<[^>]+>/g, '');
            // Count words
            wordCount = text.split(/\s+/).length;
          }

          // Assume average reading rate: 150 words per minute / adjusted for speed
          const baseWPM = 150;
          const adjustedWPM = baseWPM * this.voiceSpeed;
          const durationInMinutes = wordCount / adjustedWPM;
          const durationInSeconds = durationInMinutes * 60;

          // Store the start time, duration, and end time for this segment
          const startTime = cumulativeTime;
          cumulativeTime += durationInSeconds;

          this.segmentDurations.push({
            index,
            startTime,
            duration: durationInSeconds,
            endTime: cumulativeTime
          });
        });
      },

      // Get the width for a segment in the timeline based on its duration
      getSegmentWidth(index) {
        if (!this.segments || this.segments.length === 0) {
          return `${100 / this.totalSegments}%`;
        }

        if (this.segmentDurations === null) {
          this.calculateSegmentDurations();
        }

        // Get the segment info
        const segmentInfo = this.segmentDurations[index];

        // Calculate width based on proportion of total duration
        const totalDuration = this.segmentDurations[this.segmentDurations.length - 1].endTime;
        const proportion = segmentInfo.duration / totalDuration;

        return `${proportion * 100}%`;
      }
    }
  }
  </script>

  <style scoped>
  .podcast-controls-container {
    padding: 1rem;
    border-radius: 12px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.01));
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
  }

  .theme--dark .podcast-controls-container {
    background: linear-gradient(to bottom, rgba(30, 41, 59, 0.3), rgba(30, 41, 59, 0.1));
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  .podcast-controls-container.is-playing {
    background: linear-gradient(to bottom, rgba(66, 153, 225, 0.05), rgba(66, 153, 225, 0.01));
    box-shadow: 0 4px 25px rgba(66, 153, 225, 0.08);
  }

  .theme--dark .podcast-controls-container.is-playing {
    background: linear-gradient(to bottom, rgba(49, 130, 206, 0.1), rgba(49, 130, 206, 0.02));
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.2);
  }

  /* Timeline section */
  .timeline-section {
    margin-bottom: 1.25rem;
  }

  .segment-preview {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .current-segment-indicator {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .segment-count {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    background-color: rgba(66, 153, 225, 0.1);
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #4299e1;
  }

  .theme--dark .segment-count {
    background-color: rgba(49, 130, 206, 0.2);
    color: #63b3ed;
  }

  .current-speaker {
    font-size: 0.875rem;
    font-weight: 500;
    color: #4a5568;
  }

  .theme--dark .current-speaker {
    color: #e2e8f0;
  }

  .speaker-role {
    color: #718096;
    font-weight: 400;
  }

  .theme--dark .speaker-role {
    color: #a0aec0;
  }

  .time-display {
    font-size: 0.75rem;
    color: #718096;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .theme--dark .time-display {
    color: #a0aec0;
  }

  .time-separator {
    opacity: 0.5;
  }

  /* Progress timeline */
  .progress-timeline {
    position: relative;
    height: 6px;
    background: rgba(226, 232, 240, 0.5);
    border-radius: 3px;
    cursor: pointer;
    overflow: hidden;
  }

  .theme--dark .progress-timeline {
    background: rgba(45, 55, 72, 0.5);
  }

  .timeline-progress {
    position: absolute;
    height: 100%;
    background: linear-gradient(90deg, #4299e1, #667eea);
    border-radius: 3px;
    transition: width 0.1s linear;
    z-index: 2;
  }

  .theme--dark .timeline-progress {
    background: linear-gradient(90deg, #3182ce, #5a67d8);
  }

  .timeline-handle {
    position: absolute;
    top: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: white;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease;
    z-index: 3;
  }

  .theme--dark .timeline-handle {
    background: #e2e8f0;
  }

  .timeline-handle:hover {
    transform: translate(-50%, -50%) scale(1.2);
  }

  .progress-segments-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    display: flex;
    z-index: 1;
  }

  .segment-marker {
    height: 100%;
    opacity: 0.3;
    transition: opacity 0.3s ease;
  }

  .segment-marker.active {
    opacity: 0.8;
  }

  .segment-marker.completed {
    opacity: 0.5;
  }

  /* Control deck */
  .control-deck {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  .main-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
  }

  /* Compact speed controls */
  .compact-speed-control {
    display: flex;
    align-items: center;
    background: rgba(226, 232, 240, 0.2);
    padding: 0.25rem 0.5rem;
    border-radius: 20px;
    border: 1px solid rgba(203, 213, 224, 0.4);
  }

  .theme--dark .compact-speed-control {
    background: rgba(45, 55, 72, 0.3);
    border-color: rgba(74, 85, 104, 0.4);
  }

  .speed-label {
    font-size: 0.75rem;
    color: #4a5568;
    margin-right: 0.5rem;
  }

  .theme--dark .speed-label {
    color: #a0aec0;
  }

  .speed-buttons {
    display: flex;
    gap: 0.125rem;
  }

  .compact-speed-btn {
    padding: 0.125rem 0.375rem;
    font-size: 0.75rem;
    background: transparent;
    border: none;
    color: #718096;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .theme--dark .compact-speed-btn {
    color: #a0aec0;
  }

  .compact-speed-btn:hover {
    background: rgba(203, 213, 224, 0.3);
    color: #4a5568;
  }

  .theme--dark .compact-speed-btn:hover {
    background: rgba(74, 85, 104, 0.4);
    color: #e2e8f0;
  }

  .compact-speed-btn.active {
    background: #4299e1;
    color: white;
  }

  .theme--dark .compact-speed-btn.active {
    background: #3182ce;
  }

  /* Control buttons */
  .control-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid #e2e8f0;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .theme--dark .control-btn {
    border-color: #4a5568;
    color: #e2e8f0;
  }

  .control-btn:hover {
    background: rgba(226, 232, 240, 0.2);
    transform: translateY(-2px);
  }

  .theme--dark .control-btn:hover {
    background: rgba(74, 85, 104, 0.2);
  }

  .play-btn {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #4299e1;
    border: none;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 10px rgba(66, 153, 225, 0.3);
  }

  .theme--dark .play-btn {
    background: #3182ce;
    box-shadow: 0 4px 10px rgba(49, 130, 206, 0.4);
  }

  .play-btn:hover {
    background: #3182ce;
    transform: translateY(-2px) scale(1.05);
  }

  .play-btn:active {
    transform: translateY(0) scale(0.98);
  }

  /* Extra controls */
  .extra-controls {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }

  .control-toggle-btn {
    padding: 0.5rem;
    border-radius: 8px;
    background: transparent;
    border: 1px solid #e2e8f0;
    color: #718096;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .theme--dark .control-toggle-btn {
    border-color: #4a5568;
    color: #a0aec0;
  }

  .control-toggle-btn:hover {
    background: rgba(226, 232, 240, 0.2);
  }

  .theme--dark .control-toggle-btn:hover {
    background: rgba(74, 85, 104, 0.2);
  }

  .control-toggle-btn.active {
    background: rgba(66, 153, 225, 0.1);
    border-color: #4299e1;
    color: #4299e1;
  }

  .theme--dark .control-toggle-btn.active {
    background: rgba(49, 130, 206, 0.2);
    border-color: #3182ce;
    color: #63b3ed;
  }

  .toggle-label {
    font-size: 0.875rem;
  }

  /* Download button states */
  .download-btn.is-downloading {
    position: relative;
    padding-right: 0.75rem;
    color: #4299e1;
    border-color: #4299e1;
    background: rgba(66, 153, 225, 0.1);
    cursor: wait;
  }

  .download-btn.is-ready {
    background: #48bb78;
    border-color: #48bb78;
    color: white;
  }

  .download-progress-ring {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .progress-text {
    position: absolute;
    font-size: 0.625rem;
    font-weight: 700;
  }

  /* Audio visualization */
  .audio-visualization {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 3px;
    height: 40px;
    margin-left: 7rem;
    padding: 0.5rem;
  }

  .audio-bar {
    width: 3px;
    height: 5px;
    background: linear-gradient(to top, #4299e1, #667eea);
    border-radius: 1px;
    animation: audio-wave 1.2s ease infinite;
  }

  .audio-bar:nth-child(1) { animation-delay: 0.0s; height: 9px; }
  .audio-bar:nth-child(2) { animation-delay: 0.1s; height: 15px; }
  .audio-bar:nth-child(3) { animation-delay: 0.2s; height: 7px; }
  .audio-bar:nth-child(4) { animation-delay: 0.3s; height: 12px; }
  .audio-bar:nth-child(5) { animation-delay: 0.4s; height: 5px; }
  .audio-bar:nth-child(6) { animation-delay: 0.5s; height: 17px; }
  .audio-bar:nth-child(7) { animation-delay: 0.6s; height: 10px; }
  .audio-bar:nth-child(8) { animation-delay: 0.7s; height: 13px; }
  .audio-bar:nth-child(9) { animation-delay: 0.8s; height: 8px; }
  .audio-bar:nth-child(10) { animation-delay: 0.9s; height: 16px; }
  .audio-bar:nth-child(11) { animation-delay: 1.0s; height: 6px; }
  .audio-bar:nth-child(12) { animation-delay: 0.7s; height: 14px; }
  .audio-bar:nth-child(13) { animation-delay: 0.8s; height: 7px; }
  .audio-bar:nth-child(14) { animation-delay: 0.9s; height: 16px; }
  .audio-bar:nth-child(15) { animation-delay: 1.0s; height: 9px; }
  .audio-bar:nth-child(16) { animation-delay: 0.1s; height: 11px; }
  .audio-bar:nth-child(17) { animation-delay: 0.2s; height: 15px; }
  .audio-bar:nth-child(18) { animation-delay: 0.3s; height: 8px; }
  .audio-bar:nth-child(19) { animation-delay: 0.4s; height: 12px; }
  .audio-bar:nth-child(20) { animation-delay: 0.5s; height: 9px; }

  @keyframes audio-wave {
    0%, 100% {
      transform: scaleY(0.6);
    }
    50% {
      transform: scaleY(1.2);
    }
  }

  /* Responsive styles */
  @media (max-width: 768px) {

    .time-display {
      align-self: flex-end;
    }

    .audio-visualization {
      margin-left: 0;
      padding: 0.25rem;
    }

    .control-deck {
      flex-direction: column;
      gap: 1rem;
    }

    .secondary-controls,
    .extra-controls {
      width: 100%;
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .main-controls {
      width: 100%;
      gap: 0.5rem;
    }

    .control-btn {
      width: 36px;
      height: 36px;
    }

    .play-btn {
      width: 48px;
      height: 48px;
    }

    .control-toggle-btn {
      padding: 0.375rem;
      font-size: 0.75rem;
    }

    .toggle-label {
      display: none;
    }
  }

  .button-container {
  display: flex;
  align-items: center;
}

.expanded-view-toggle {
  background-color: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.expanded-view-toggle:hover {
  background-color: rgba(226, 232, 240, 0.2);
}

/* Make the button stand out a bit more */
.expanded-view-toggle {
  position: relative;
  padding: 8px;
  border-radius: 50%;
  border: 1px solid transparent;
  background: rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.expanded-view-toggle:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: scale(1.05);
}

/* Add a subtle rotating animation to the settings icon */
.settings-icon {
  animation: subtle-rotate 8s infinite linear;
}

@keyframes subtle-rotate {
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
}

/* Attention-grabbing pulse animation for first-time users */
.pulse-animation {
  animation: pulse 2s infinite;
  box-shadow: 0 0 0 rgba(0, 123, 255, 0.4);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 123, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
  }
}

/* Small notification badge */
.customization-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 14px;
  height: 14px;
  background: #ff5252;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}
  </style>
